import React, { useState, useContext } from 'react';
import classNames from 'classnames/bind';
import { Button, Icon, Textarea, Typography } from 'gd-design-library';
import { ImageUploadButton, SearchSendButton, VoiceInputButton, SearchImagePreview } from '@components';
import styles from './ChatInput.module.scss';
import { useRouter } from 'next/router';
import { useServiceRef } from '../../../../hooks';
import { SearchService } from '@services';
import { ImageToQueryResponse } from '../../../../services/search/interfaces';
import { fileToBase64 } from '../../utils';
import { ChatMessageType } from '../../../../utils/SmartSearchContext';
import { SnackbarContext } from '@utils';
import { MAX_FILE_SIZE_MB } from '../../constants/imageUpload';

const cx = classNames.bind(styles);
const CN = 'chat-input';
const containerClassName = `${CN}__container`;
const inputContainerClassName = `${CN}__input-container`;
const dragOverlayClassName = `${CN}__drag-overlay`;
const inputBoxClassName = `${CN}__input-box`;
const inputRowClassName = `${CN}__input-row`;
const chatInputFieldClassName = `${CN}__chat-input-field`;
const backButtonClassName = `${CN}__back-button`;

interface ChatInputProps {
  isLoading: boolean;
  onSendMessage: (message: string, image?: File) => Promise<void>;
  addChatMessage: (content: string, type: ChatMessageType, image?: File) => void;
  resetSuggestedAnswers: () => void;
  setIsLoading: (loading: boolean) => void;
  className?: string;
}


export const ChatInput: React.FC<ChatInputProps> = ({
  isLoading,
  onSendMessage,
  addChatMessage,
  resetSuggestedAnswers,
  setIsLoading,
  className
}) => {
  const [ chatInput, setChatInput ] = useState('');
  const [ uploadedImage, setUploadedImage ] = useState<File | null>(null);
  const [ isDragging, setIsDragging ] = useState(false);
  const [ isVoiceListening, setIsVoiceListening ] = useState(false);
  const router = useRouter();
  const searchServiceRef = useServiceRef(SearchService);
  const { setSnackbarParams } = useContext(SnackbarContext);

  const handleChatInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setChatInput(event.target.value);
  };

  const showError = (message: string): void => {
    setSnackbarParams({
      message,
      severity: 'warning',
      open: true,
      onClose: () => setSnackbarParams({ open: false, message: '', onClose: () => null })
    });
  };

  const validateAndSetImage = (file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showError('Please drop a valid image file.');

      return false;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showError(`Image is too large (max ${MAX_FILE_SIZE_MB}MB).`);

      return false;
    }

    setUploadedImage(file);

    return true;
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file && !isLoading) {
      validateAndSetImage(file);
    }
    event.dataTransfer.clearData();
  };

  const handleRedirectToSearchPage = async (): Promise<void> => {
    const userMessage = chatInput.trim();
    let imageToQueryResult: ImageToQueryResponse | undefined;

    if (uploadedImage) {
      setIsLoading(true);
      try {
        const image = uploadedImage;
        setUploadedImage(null);
        setChatInput('');
        addChatMessage(userMessage, ChatMessageType.USER, image);
        imageToQueryResult = await searchServiceRef.current.imageToQuery(image, userMessage);
        const base64Image = await fileToBase64(image);
        sessionStorage.setItem('smartSearchUploadedImage', base64Image);
        sessionStorage.setItem('smartSearchImageToQueryResult', imageToQueryResult.imageToQuery);
        sessionStorage.setItem('smartSearchUserMessage', userMessage);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error converting image to base64:', error);
        setIsLoading(false);
      }
      setIsLoading(false);
    } else {
      addChatMessage(userMessage, ChatMessageType.USER);
      setChatInput('');
    }

    const encodedQuery = encodeURIComponent(imageToQueryResult?.imageToQuery || userMessage);
    await router.push(`/search/${encodedQuery}?conversational-search=true`);
  };

  const handleSendMessage = async (): Promise<void> => {
    if ((chatInput.trim() || uploadedImage) && !isLoading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const userMessage = chatInput.trim();
      let message = userMessage;

      resetSuggestedAnswers();

      const isOnSearchPage = router.pathname.startsWith('/search');
      const { searchQuery } = router.query;

      // Redirect to search page if user starts a new conversation on other pages
      // or on page query without searchQuery (e.g. search by image from search bar)
      if ((!isOnSearchPage && (userMessage || uploadedImage)) || (isOnSearchPage && !searchQuery)) {
        await handleRedirectToSearchPage();

        return;
      }

      if (uploadedImage) {
        setIsLoading(true);
        try {
          const image = uploadedImage;
          setUploadedImage(null);
          setChatInput('');
          addChatMessage(userMessage, ChatMessageType.USER, image);

          const searchService = searchServiceRef.current;
          const imageToQueryResult = await searchService.imageToQuery(image, userMessage);

          if (imageToQueryResult) {
            message = imageToQueryResult.imageToQuery;
          } else {
            addChatMessage('Failed to process image. Please try again.', ChatMessageType.AI);
            setIsLoading(false);

            return;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error processing image:', error);
          addChatMessage('Error processing image. Please try again.', ChatMessageType.AI);
          setIsLoading(false);

          return;
        }
        setIsLoading(false);
      } else if (userMessage) {
        addChatMessage(userMessage, ChatMessageType.USER);
        setChatInput('');
      }

      await onSendMessage(message, uploadedImage);
    }
  };


  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    const items = event.clipboardData?.items;
    if (!items?.length) return;

    const file = Array.from(items)
      .find(item => item.kind === 'file' && item.type.startsWith('image/'))
      ?.getAsFile();

    if (!file) return;

    const normalizedFile = new File(
      [ file ],
      file.name || 'pasted-image.png',
      { type: file.type || 'image/png' }
    );

    event.preventDefault();
    setUploadedImage(normalizedFile);
  };

  return (
    <div className={ cx(containerClassName, className) }>
      <div
        className={ cx(inputContainerClassName) }
        onDragOver={ handleDragOver }
        onDragLeave={ handleDragLeave }
        onDrop={ handleDrop }
      >
        {isDragging && (
          <div className={ cx(dragOverlayClassName) }>
            <Icon name='attachment' width={ 24 } height={ 24 } />
            <Typography variant='strong'>Drop image here</Typography>
          </div>
        )}
        <div className={ cx(inputBoxClassName) }>
          <SearchImagePreview
            uploadedImage={ uploadedImage }
            setUploadedImage={ setUploadedImage }
          />
          <div className={ cx(inputRowClassName) }>
            <Button
              variant='inherit'
              isIcon
              onClick={ () => router.back() }
              className={ cx(backButtonClassName) }
              aria-label='Close search'
            >
              <Icon
                name='arrowForward'
                width={ 16 }
                height={ 16 }
                style={ { transform: 'rotate(180deg)' } }
              />
            </Button>
            <Textarea
              name='chatInput'
              placeholder='Write a message...'
              value={ chatInput }
              onChange={ handleChatInputChange }
              onKeyDown={ handleKeyDown }
              onPaste={ handlePaste }
              className={ cx(chatInputFieldClassName) }
              disabled={ isLoading }
              rows={ 1 }
              maxLength={ 150 }
            />
            {(chatInput.trim().length === 0 || isVoiceListening) && (
              <VoiceInputButton
                onTranscript={ setChatInput }
                isListening={ isVoiceListening }
                onListeningChange={ setIsVoiceListening }
                disabled={ isLoading }
              />
            )}
            <ImageUploadButton
              setUploadedImage={ setUploadedImage }
              disabled={ isLoading }
            />
            {((chatInput.trim().length > 0 || uploadedImage) && !isVoiceListening) && (
              <SearchSendButton
                disabled={ (!chatInput.trim() && !uploadedImage) || isLoading }
                onClick={ handleSendMessage }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
